
import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import DateNavigation from '@/components/memories/DateNavigation';
import MemoryList from '@/components/memories/MemoryList';
import MemoryTimeline from '@/components/memories/MemoryTimeline';
import { Memory } from '@/types/memory';
import { format, isSameMonth, isSameDay, isValid } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getAllMemories } from '@/utils/storage';
import { logger } from '@/utils/logger';

const OnThisDay = () => {
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [memories, setMemories] = useState<Memory[]>([]);
  const [todayMemories, setTodayMemories] = useState<Memory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    // Load all memories from localStorage
    const fetchMemories = async () => {
      try {
        setIsLoading(true);
        const allMemories = await getAllMemories();
        setMemories(allMemories);
        
        // Filter memories for the current date (same day/month, any year)
        filterMemoriesForDate(allMemories, selectedDate);
      } catch (error) {
        logger.error("Error fetching memories:", error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchMemories();
  }, [selectedDate]);
  
  const filterMemoriesForDate = (allMemories: Memory[], date: Date) => {
    const filtered = allMemories.filter(memory => {
      try {
        const memoryDate = new Date(memory.date);

        // Verify the date is valid before comparing
        if (!isValid(memoryDate)) {
          logger.warn(`Invalid date found in memory: ${memory.id}`, memory.date);
          return false;
        }

        // Check if same calendar day and month (ignoring year)
        return memoryDate.getMonth() === date.getMonth() &&
               memoryDate.getDate() === date.getDate();
      } catch (error) {
        logger.error("Error processing memory date:", error, memory);
        return false;
      }
    });

    // Sort by date chronologically (oldest to newest)
    const sorted = filtered.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return dateA - dateB;
    });

    setTodayMemories(sorted);
  };
  
  const handleSelectMemory = (date: Date) => {
    if (isValid(date)) {
      setSelectedDate(date);
    }
  };

  return (
    <Layout>
      <div className="pb-16">
        <div className="mb-6 text-center">
          <h1 className="text-2xl font-bold mb-4">On This Day</h1>
          <p className="text-gray-500">
            See what happened on {format(selectedDate, 'MMMM d')} throughout the years
          </p>
          
          <div className="mt-4">
            <DateNavigation 
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
            />
          </div>
        </div>
        
        <Tabs defaultValue="memories" className="mt-6">
          <TabsList>
            <TabsTrigger value="memories">Memories</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>
          
          <TabsContent value="memories" className="mt-4">
            {isLoading ? (
              <div className="text-center py-10">
                <p>Loading memories...</p>
              </div>
            ) : (
              <MemoryList memories={todayMemories} />
            )}
          </TabsContent>
          
          <TabsContent value="timeline" className="mt-4">
            {isLoading ? (
              <div className="text-center py-10">
                <p>Loading timeline...</p>
              </div>
            ) : (
              <MemoryTimeline 
                memories={memories} 
                onSelectMemory={handleSelectMemory}
              />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
};

export default OnThisDay;
